import React from 'react';
import { Modal, TextInput, Select, Pagination, Loader, UnstyledButton } from '@mantine/core';
import { formatMoney } from '../../../../utils/money';
import { usePastReceiptsController } from './usePastReceiptsController';
import { formatClosedAt } from './past-receipts.utils';
import type { PastReceiptsModalProps } from './past-receipts.types';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import classes from './past-receipts.module.scss';

// Summary: Manager past-receipts browser — paginated closed orders and filters; row → receipt preview.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-past-receipts
const PastReceiptsModalComponent: React.FC<PastReceiptsModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
  onSelectOrder,
}) => {
  const c = usePastReceiptsController({ opened, tenantId, employeeSession });

  const sessionData = c.sessionOptions.map((option) => ({
    value: String(option.id),
    label: option.name,
  }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<span className={classes.title}>קבלות עבר</span>}
      size="xl"
      centered
      radius="md"
      dir="rtl"
      closeButtonProps={{ 'data-testid': 'past-receipts-close' } as Record<string, string>}
      classNames={{ root: navyModal.root }}
      overlayProps={{ opacity: 0.55, blur: 2 }}
    >
      <div className={classes.shell}>
        <div className={classes.filters}>
          <TextInput
            type="date"
            data-testid="past-receipts-date-from"
            label="מתאריך"
            value={c.filters.dateFrom}
            onChange={(event) => c.setFilterField('dateFrom', event.currentTarget.value)}
            radius="md"
          />
          <TextInput
            type="date"
            data-testid="past-receipts-date-to"
            label="עד תאריך"
            value={c.filters.dateTo}
            onChange={(event) => c.setFilterField('dateTo', event.currentTarget.value)}
            radius="md"
          />
          <TextInput
            data-testid="past-receipts-search"
            label="מספר הזמנה / שולחן"
            placeholder="חיפוש"
            value={c.filters.search}
            onChange={(event) => c.setFilterField('search', event.currentTarget.value)}
            radius="md"
          />
          <TextInput
            data-testid="past-receipts-customer"
            label="שם לקוח"
            placeholder="חיפוש"
            value={c.filters.customer}
            onChange={(event) => c.setFilterField('customer', event.currentTarget.value)}
            radius="md"
          />
          <TextInput
            data-testid="past-receipts-card-last4"
            label="4 ספרות אחרונות באשראי"
            value={c.filters.cardLast4}
            inputMode="numeric"
            maxLength={4}
            onChange={(event) => c.setFilterField('cardLast4', event.currentTarget.value.replace(/\D/g, '').slice(0, 4))}
            onContextMenu={(event) => event.preventDefault()}
            styles={{ input: { userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' } }}
            radius="md"
          />
          <TextInput
            data-testid="past-receipts-waiter"
            label="שם מלצר"
            placeholder="חיפוש"
            value={c.filters.waiter}
            maxLength={120}
            onChange={(event) => c.setFilterField('waiter', event.currentTarget.value)}
            onContextMenu={(event) => event.preventDefault()}
            styles={{ input: { userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' } }}
            radius="md"
          />
          <Select
            data-testid="past-receipts-session"
            label="משמרת"
            placeholder="הכל"
            data={sessionData}
            value={c.filters.sessionId != null ? String(c.filters.sessionId) : null}
            onChange={(value) => c.setFilterField('sessionId', value ? Number(value) : null)}
            clearable
            searchable
            radius="md"
          />
        </div>

        {c.isLoading ? (
          <div className={classes.statusArea}>
            <Loader size="sm" />
          </div>
        ) : c.error ? (
          <p className={classes.error}>{c.error}</p>
        ) : c.items.length === 0 ? (
          <p className={classes.empty}>לא נמצאו קבלות</p>
        ) : (
          <div className={classes.list}>
            {c.items.map((item) => (
              <UnstyledButton
                key={item.orderId}
                type="button"
                data-testid={`past-receipts-row-${item.orderId}`}
                className={classes.row}
                onClick={() => onSelectOrder(item.orderId, item.isCancelled)}
              >
                <span className={classes.rowMain}>
                  <span className={classes.rowCode}>{item.orderCode}</span>
                  <span className={classes.rowMeta}>
                    {item.tableName ? `שולחן ${item.tableName}` : 'לקחת'}
                    {item.areaName ? ` · ${item.areaName}` : ''}
                    {item.openedByName ? ` · מלצר ${item.openedByName}` : ''}
                  </span>
                </span>
                <span className={classes.rowSide}>
                  <span className={classes.rowAmount}>{formatMoney(item.totalAmount)} ₪</span>
                  <span className={classes.rowDate}>{formatClosedAt(item.closedAt)}</span>
                </span>
                {item.customerName && <span className={classes.rowCustomer}>{item.customerName}</span>}
              </UnstyledButton>
            ))}
          </div>
        )}

        {c.pageCount > 1 && (
          <div className={classes.pagination}>
            <Pagination data-testid="past-receipts-pagination" total={c.pageCount} value={c.page} onChange={c.setPage} size="sm" />
          </div>
        )}
      </div>
    </Modal>
  );
};

export const PastReceiptsModal = React.memo(PastReceiptsModalComponent);
